import { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const interceptorRef = useRef(null);

  const handleLogout = useCallback(async ({ callApi = true } = {}) => {
    if (callApi) {
      try {
        await axios.post(`${API}/auth/logout`);
      } catch {
        /* ignore — cookie may already be gone */
      }
    }
    setUser(null);
  }, []);

  // 401 interceptor: clear session and bounce to /login.
  // CRITICAL: only fire the redirect when the user WAS authenticated (had a `user`).
  // The initial `/auth/me` call on /login pages also returns 401 "Token inválido" —
  // we must NOT treat that as a session-expiry redirect, otherwise we end up in a loop
  // every time a logged-out visitor lands on the app.
  const userRef = useRef(null);
  useEffect(() => {
    userRef.current = user;
  }, [user]);

  useEffect(() => {
    if (interceptorRef.current !== null) {
      axios.interceptors.response.eject(interceptorRef.current);
    }
    interceptorRef.current = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          const errorDetail = error.response?.data?.detail || "";
          const isAuthExpiry =
            errorDetail.includes("Token expirado") ||
            errorDetail.includes("Token inválido") ||
            errorDetail.includes("Utilizador não encontrado");
          // Only redirect if there was an active session before this 401
          // AND we're not on a public "shared" page (candidates viewing simulation links).
          const isSharedPage = window.location.pathname.startsWith("/simulator/shared");
          if (isAuthExpiry && userRef.current && !isSharedPage) {
            setUser(null);
            const currentPath = window.location.pathname;
            if (currentPath !== "/login" && currentPath !== "/" && currentPath !== "/register") {
              toast.error("Sessão expirada. Por favor, faça login novamente.");
              window.location.href = "/login";
            }
          }
        }
        return Promise.reject(error);
      }
    );
    return () => {
      if (interceptorRef.current !== null) {
        axios.interceptors.response.eject(interceptorRef.current);
      }
    };
  }, []);

  // Check existing session by calling /auth/check (cookie carries the auth).
  // Uses /auth/check (not /auth/me) — always returns 200, so no decorative 401's
  // appear in DevTools for logged-out visitors.
  useEffect(() => {
    const initAuth = async () => {
      try {
        const response = await axios.get(`${API}/auth/check`);
        if (response.data?.authenticated && response.data?.user) {
          setUser(response.data.user);
        } else {
          // No active session — keep whatever `login()` may have set (no-op).
          setUser((prev) => prev);
        }
      } catch (error) {
        console.error("Auth init error:", error);
        setUser((prev) => prev);
      } finally {
        setLoading(false);
      }
    };
    initAuth();
  }, []);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { user: userData } = response.data;
    setUser(userData);
    return userData;
  };

  const register = async (name, email, password, role = "user") => {
    const response = await axios.post(`${API}/auth/register`, {
      name,
      email,
      password,
      role,
    });
    const { user: userData } = response.data;
    setUser(userData);
    return userData;
  };

  const logout = () => {
    handleLogout();
  };

  // Refresh current user from backend (after profile update, etc.)
  const refreshUser = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/auth/me`);
      setUser((prev) => ({ ...(prev || {}), ...r.data }));
      return r.data;
    } catch {
      return null;
    }
  }, []);

  // Kept for backwards compat — returns empty so existing call sites don't break.
  // Auth now travels via httpOnly cookie (axios withCredentials=true is set globally).
  const getAuthHeaders = useCallback(() => ({}), []);

  return (
    <AuthContext.Provider
      value={{
        user,
        token: null, // legacy — kept for API stability; cookie does the work now
        loading,
        login,
        register,
        logout,
        getAuthHeaders,
        refreshSession: () => {},
        refreshUser,
        isAdmin: user?.role === "admin",
        isAuthenticated: !!user,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
